import { useState } from "react";
import { Banner, Button, Drawer, Field, Input, SegmentedControl, TextArea, Toggle } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateAppConfigMutation } from "../services/useCreateAppConfigMutation";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** After a create, with the new key (for the toast and the Custom tab). */
  onCreated: (key: string) => void;
};

type DraftType = "boolean" | "integer" | "string" | "url";
type DraftScope = "runtime" | "boot";

type Draft = {
  key: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  type: DraftType;
  scope: DraftScope;
  value: string;
  minInt: string;
  maxInt: string;
};

const EMPTY_DRAFT: Draft = {
  key: "",
  nameEn: "",
  nameAr: "",
  descriptionEn: "",
  descriptionAr: "",
  type: "boolean",
  scope: "runtime",
  value: "false",
  minInt: "",
  maxInt: "",
};

const FORM_ID = "app-config-form";
// What the key helper promises; the backend enforces the same rule.
const KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/**
 * Creates a custom (non-registry) key, in a drawer over the configs. The form
 * mirrors the metadata registry keys carry in code: bilingual name and
 * description, type, scope, value and integer bounds. The backend stores all
 * of it, so the list renders custom keys with the same controls as registry
 * keys. (The file keeps its old name so imports stay put.)
 */
export function AddAppConfigDialog({ isOpen, onClose, onCreated }: Props) {
  const t = useTranslations();
  const { mutateAsync, isPending } = useCreateAppConfigMutation();
  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      width={540}
      badge="key"
      crumb={`${t(keys.appConfigs.title)} / ${t(keys.appConfigs.sectionCustom)}`}
      title={t("appConfigs.addTitleNew")}
      sub={t("appConfigs.addSubNew")}
      actions={
        <Button type="submit" form={FORM_ID} variant="primary" size="sm" loading={isPending} loadingLabel={t("appConfigs.creating")}>
          {t("appConfigs.createKeyAction")}
        </Button>
      }
    >
      <CustomKeyForm onCreated={onCreated} create={mutateAsync} isPending={isPending} />
    </Drawer>
  );
}

function CustomKeyForm({
  onCreated,
  create: mutateAsync,
  isPending,
}: {
  onCreated: (key: string) => void;
  /** The drawer owns the mutation so its bar's button can show the request running. */
  create: ReturnType<typeof useCreateAppConfigMutation>["mutateAsync"];
  isPending: boolean;
}) {
  const t = useTranslations();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const update = <K extends keyof Draft>(field: K, value: Draft[K]) => setDraft((prev) => ({ ...prev, [field]: value }));

  const setType = (type: DraftType) =>
    setDraft((prev) => ({
      ...prev,
      type,
      // A boolean always has a value (the toggle); leaving boolean drops it.
      value: type === "boolean" ? (prev.value === "true" ? "true" : "false") : prev.type === "boolean" ? "" : prev.value,
    }));

  const isInteger = draft.type === "integer";
  const isBoolean = draft.type === "boolean";
  const required = t(keys.errors.required);
  const keyError = !draft.key ? required : !KEY_PATTERN.test(draft.key) ? t(keys.appConfigs.addKeyHelper) : undefined;
  const fieldErrors = tried
    ? {
        key: keyError,
        nameEn: draft.nameEn.trim() ? undefined : required,
        nameAr: draft.nameAr.trim() ? undefined : required,
        value: draft.value.length ? undefined : required,
      }
    : {};

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending) return;
    setTried(true);
    if (keyError || !draft.nameEn.trim() || !draft.nameAr.trim() || draft.value.length === 0) {
      setError(null);
      return;
    }
    try {
      await mutateAsync({
        key: draft.key.trim(),
        nameEn: draft.nameEn.trim(),
        nameAr: draft.nameAr.trim(),
        descriptionEn: draft.descriptionEn.trim(),
        descriptionAr: draft.descriptionAr.trim(),
        type: draft.type,
        scope: draft.scope,
        value: draft.value,
        ...(isInteger && draft.minInt.trim().length > 0 ? { minInt: Number(draft.minInt) } : {}),
        ...(isInteger && draft.maxInt.trim().length > 0 ? { maxInt: Number(draft.maxInt) } : {}),
      });
      const created = draft.key.trim();
      setDraft(EMPTY_DRAFT);
      setTried(false);
      setError(null);
      onCreated(created);
    } catch (err) {
      setError(err instanceof ApiJsonClientError ? err.message || t(keys.errors.generic) : t(keys.errors.generic));
    }
  };

  return (
    <form id={FORM_ID} onSubmit={handleSubmit} noValidate aria-busy={isPending || undefined} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {error ? (
        <Banner
          tone="error"
          title={t("appConfigs.createFailedTitle")}
          actions={
            <Button type="submit" size="sm" loading={isPending} loadingLabel={t(keys.shell.retrying)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {`${error} ${t("appConfigs.nothingChanged")}`}
        </Banner>
      ) : null}
      <Field label={t(keys.appConfigs.addKeyLabel)} help={t(keys.appConfigs.addKeyHelper)} error={fieldErrors.key} htmlFor="app-config-key">
        <Input
          id="app-config-key"
          name="key"
          dir="ltr"
          autoFocus
          placeholder="MAX_CART_ITEMS"
          value={draft.key}
          invalid={Boolean(fieldErrors.key)}
          onChange={(event) => update("key", event.target.value.toUpperCase().replace(/\s+/g, "_"))}
          style={{ maxWidth: 320 }}
        />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 12 }}>
        <Field label={t(keys.appConfigs.addNameEnLabel)} error={fieldErrors.nameEn} htmlFor="app-config-name-en">
          <Input id="app-config-name-en" name="nameEn" dir="ltr" value={draft.nameEn} invalid={Boolean(fieldErrors.nameEn)} onChange={(event) => update("nameEn", event.target.value)} />
        </Field>
        <Field label={t(keys.appConfigs.addNameArLabel)} error={fieldErrors.nameAr} htmlFor="app-config-name-ar">
          <Input id="app-config-name-ar" name="nameAr" dir="rtl" value={draft.nameAr} invalid={Boolean(fieldErrors.nameAr)} onChange={(event) => update("nameAr", event.target.value)} />
        </Field>
      </div>
      <Field label={`${t(keys.appConfigs.addDescriptionEnLabel)} ${t("appConfigs.optionalSuffix")}`} htmlFor="app-config-desc-en">
        <TextArea id="app-config-desc-en" name="descriptionEn" dir="ltr" rows={2} value={draft.descriptionEn} onChange={(event) => update("descriptionEn", event.target.value)} />
      </Field>
      <Field label={`${t(keys.appConfigs.addDescriptionArLabel)} ${t("appConfigs.optionalSuffix")}`} htmlFor="app-config-desc-ar">
        <TextArea id="app-config-desc-ar" name="descriptionAr" dir="rtl" rows={2} value={draft.descriptionAr} onChange={(event) => update("descriptionAr", event.target.value)} />
      </Field>
      <Field label={t(keys.appConfigs.addTypeLabel)}>
        <SegmentedControl<DraftType>
          label={t(keys.appConfigs.addTypeLabel)}
          value={draft.type}
          onChange={setType}
          options={[
            { value: "boolean", label: t("appConfigs.typeOnOff") },
            { value: "integer", label: t("appConfigs.typeWholeNumber") },
            { value: "string", label: t(keys.appConfigs.typeString) },
            { value: "url", label: t(keys.appConfigs.typeUrl) },
          ]}
        />
      </Field>
      <Field label={t("appConfigs.scopeWhenRead")} help={t("appConfigs.scopeWhenReadHelp")}>
        <SegmentedControl<DraftScope>
          label={t("appConfigs.scopeWhenRead")}
          value={draft.scope}
          onChange={(scope) => update("scope", scope)}
          options={[
            { value: "runtime", label: t("appConfigs.scopeLive") },
            { value: "boot", label: t("appConfigs.scopeStartup") },
          ]}
        />
      </Field>
      {isInteger ? (
        <div className="rd-fields-inline">
          <Field label={`${t(keys.appConfigs.addMinLabel)} ${t("appConfigs.optionalSuffix")}`} htmlFor="app-config-min">
            <Input id="app-config-min" name="minInt" type="number" inputMode="numeric" dir="ltr" value={draft.minInt} onChange={(event) => update("minInt", event.target.value)} style={{ width: 130 }} />
          </Field>
          <Field label={`${t(keys.appConfigs.addMaxLabel)} ${t("appConfigs.optionalSuffix")}`} htmlFor="app-config-max">
            <Input id="app-config-max" name="maxInt" type="number" inputMode="numeric" dir="ltr" value={draft.maxInt} onChange={(event) => update("maxInt", event.target.value)} style={{ width: 130 }} />
          </Field>
        </div>
      ) : null}
      {isBoolean ? (
        <Field label={t(keys.appConfigs.addValueLabel)}>
          <Toggle label={t(keys.appConfigs.addValueLabel)} checked={draft.value === "true"} onChange={(on) => update("value", on ? "true" : "false")} />
        </Field>
      ) : (
        <Field label={t(keys.appConfigs.addValueLabel)} error={fieldErrors.value} htmlFor="app-config-value">
          <Input
            id="app-config-value"
            name="value"
            dir="ltr"
            type={isInteger ? "number" : draft.type === "url" ? "url" : "text"}
            inputMode={isInteger ? "numeric" : undefined}
            value={draft.value}
            invalid={Boolean(fieldErrors.value)}
            onChange={(event) => update("value", event.target.value)}
            style={isInteger ? { width: 160 } : { maxWidth: 420 }}
          />
        </Field>
      )}
    </form>
  );
}
